import { prettyDateTime } from "@app/date";
import {
  fetchExternalAwsAccountById,
  fetchOperationById,
  fetchOperationsByExternalAwsAccountId,
  selectExternalAwsAccountById,
  selectIsUserOwner,
  selectLatestSyncOpByExternalAwsAccountId,
  syncExternalAwsAccount,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import {
  operationDetailUrl,
  settingsAccountIntegrationsAwsDetailUrl,
  settingsAccountIntegrationsAwsResourcesUrl,
  settingsAccountIntegrationsAwsSettingsUrl,
  settingsAccountIntegrationsUrl,
} from "@app/routes";
import type { DeployExternalAwsAccount } from "@app/types";
import { useExternalAwsAccountSyncOpsPoller } from "@app/ui/hooks";
import { Link, Outlet, useParams } from "react-router-dom";
import {
  Button,
  DetailHeader,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHeaderView,
  DetailTitleBar,
  ExternalLink,
  IconAws,
  IconExternalLink,
  IconRefresh,
  OpStatus,
  type TabItem,
  Tooltip,
} from "../shared";

const ownerSyncTooltip =
  "Only organization owners can sync accounts. Ask an org owner to run this sync or grant you owner access.";

export function ExternalAwsAccountHeader({
  account,
  isLoading,
}: {
  account: DeployExternalAwsAccount;
  isLoading: boolean;
}) {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const isOwner = useSelector((s) => selectIsUserOwner(s, { orgId }));
  const syncOp = useSelector((s) =>
    selectLatestSyncOpByExternalAwsAccountId(s, { id: account.id }),
  );
  const syncAction = syncExternalAwsAccount({ id: account.id });
  const syncLoader = useLoader(syncAction);
  const syncOpId = syncOp.id || syncLoader.meta?.operationId || "";
  const isSyncing =
    syncLoader.isLoading ||
    syncOp.status === "queued" ||
    syncOp.status === "running";
  const syncDisabled = !isOwner || isSyncing || account.id === "";

  useLoaderSuccess(syncLoader, () => {
    if (syncLoader.meta?.operationId) {
      dispatch(fetchOperationById({ id: syncLoader.meta.operationId }));
    }
  });

  const syncButton = (
    <Button
      variant="white"
      size="sm"
      disabled={syncDisabled}
      onClick={() => dispatch(syncAction)}
    >
      <IconRefresh variant="sm" className="inline mr-2 h-5 mt-0" />
      Sync
    </Button>
  );

  return (
    <DetailHeader>
      <DetailTitleBar
        otherButtonsBeforeDocs={
          <div className="mr-2">
            {isOwner ? (
              syncButton
            ) : (
              <Tooltip text={ownerSyncTooltip}>{syncButton}</Tooltip>
            )}
          </div>
        }
        docsUrl="#"
        title="Account Details"
        isLoading={isLoading}
        icon={<IconAws />}
      />

      <DetailInfoGrid>
        <DetailInfoItem title="Account ID">
          <ExternalLink
            href={`https://${account.awsAccountId}.signin.aws.amazon.com/console`}
          >
            <span className="leading-5 pt-1">{account.awsAccountId}</span>
            <IconExternalLink
              variant="sm"
              className="inline pt-0 ml-1 h-5 mb-1 stroke-current"
            />
          </ExternalLink>
        </DetailInfoItem>

        <DetailInfoItem title="Last Synced">
          {isSyncing ? (
            <div className="flex items-center gap-2">
              <OpStatus status={syncOp.status} />
              <span>Syncing...</span>
            </div>
          ) : account.lastDiscoveryAt ? (
            prettyDateTime(account.lastDiscoveryAt)
          ) : (
            <em>Not yet synced</em>
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Latest Sync Operation">
          {syncOpId ? (
            <Link to={operationDetailUrl(syncOpId)}>{syncOpId}</Link>
          ) : (
            <span>None</span>
          )}
          {syncOpId ? (
            <div className="text-gray-500">
              <OpStatus status={syncOp.status} />
            </div>
          ) : null}
        </DetailInfoItem>

        <DetailInfoItem title="Created">
          {prettyDateTime(account.createdAt)}
        </DetailInfoItem>
      </DetailInfoGrid>
    </DetailHeader>
  );
}

function ExternalAwsAccountPageHeader() {
  const { id = "" } = useParams();
  const account = useSelector((s) => selectExternalAwsAccountById(s, { id }));
  const loader = useQuery(fetchExternalAwsAccountById({ id }));
  const load = useLoader(fetchExternalAwsAccountById({ id }));
  useExternalAwsAccountSyncOpsPoller([id]);
  useQuery(
    fetchOperationsByExternalAwsAccountId({
      id,
      page: 1,
      resourceType: "ExternalAwsAccount",
      operationType: "sync",
      operationStatus: "",
      startDate: "",
      endDate: "",
    }),
  );

  const tabs: TabItem[] = [
    { name: "Resources", href: settingsAccountIntegrationsAwsResourcesUrl(id) },
    { name: "Settings", href: settingsAccountIntegrationsAwsSettingsUrl(id) },
  ];

  return (
    <DetailPageHeaderView
      {...loader}
      breadcrumbs={[
        {
          name: "Account Integrations",
          to: settingsAccountIntegrationsUrl(),
        },
      ]}
      title={account.accountName || account.id}
      detailsBox={
        <ExternalAwsAccountHeader
          account={account}
          isLoading={load.isLoading}
        />
      }
      tabs={tabs}
      lastBreadcrumbTo={settingsAccountIntegrationsAwsDetailUrl(id)}
    />
  );
}

export const ExternalAwsAccountDetailLayout = () => {
  return (
    <>
      <ExternalAwsAccountPageHeader />
      <Outlet />
    </>
  );
};
